<template>
  <div class="monetary-dashboard" :class="{ 'monetary-dashboard--dark': isDarkSkin }">

    <div class="monetary-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard single-row v-model="filter" v-bind:fields="filterFields"/>
      <calendar-bar v-model="dateRange" @input="scheduleRefresh"/>
    </div>
    <!-- One-click department pills — fill the BU filter like the sales dashboard -->
    <dept-quick-filter v-model="filter.buIds" :org-ids="filter.orgIds"/>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <div class="kpi-grid">
        <!-- Hero (Command Center layout): TOTAL deposits is the headline.
             Inside it: Net (with its delta + withdrawal rate) and the pending
             deposits — each part opens its own transaction list. Same filters
             as the Deposits table below — modal, table and card always agree. -->
        <div class="kpi-cell kpi-cell--hero">
          <div class="kpi-card kpi-card--success mon-hero">
            <div class="mon-hero__body">
              <button type="button" class="mon-hero__main" v-b-tooltip.hover
                      :title="$t('monetaryDashboard.viewTransactions')" @click="openCardTx($t('Total Deposits'), depositsFilters)">
                <span class="kpi-card__title">{{ $t('Total Deposits') }}<i class="fas fa-expand mon-hero__expand"></i></span>
                <span class="mon-hero__value">{{ convertToEURO(statistics.totalDeposits) }}</span>
                <span class="mon-hero__meta">
                  <span><feather-icon icon="HashIcon" size="12" class="mr-25"/>{{ $t('Count') }}: {{ statistics.depositsCount }}</span>
                </span>
              </button>

              <!-- Net = deposits − withdrawals: its list holds BOTH kinds, the modal's sum is the net -->
              <button type="button" class="mon-hero__net" v-b-tooltip.hover
                      :title="$t('monetaryDashboard.viewTransactions')"
                      @click="openCardTx($t('Net Deposits'), { ...depositsFilters, type: null })">
                <span class="mon-hero__net-label"><feather-icon icon="TrendingUpIcon" size="14" class="mr-25"/>{{ $t('Net Deposits') }}</span>
                <span class="mon-hero__net-value">{{ convertToEURO(statistics.netDeposits) }}</span>
                <span class="mon-hero__net-rate"><feather-icon icon="PercentIcon" size="12" class="mr-25"/>{{ $t('Withdrawal Rate') }}: {{ withdrawalRate }}%</span>
                <i class="fas fa-expand mon-hero__expand ml-25"></i>
              </button>

              <!-- Pending deposits (REQUEST/PENDING) → the review modal -->
              <button type="button" class="mon-hero__pending" v-b-tooltip.hover
                      :title="$t('finance.clickToReview')" @click="openPending('DEPOSIT')">
                <span class="mon-hero__pending-label"><i class="fa-regular fa-clock mr-25"></i>{{ $t('monetaryDashboard.pendingDeposits') }}</span>
                <span class="mon-hero__pending-count">{{ statistics.pendingDepositsCount }}</span>
                <span class="mon-hero__pending-amount">{{ convertToEURO(statistics.pendingDepositsAmount) }}</span>
                <i class="fa-solid fa-chevron-right mon-hero__pending-arrow"></i>
              </button>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <div class="kpi-cell">
          <div class="kpi-card kpi-card--danger kpi-card--clickable" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
               @click="openCardTx($t('Total Withdrawals'), withdrawalsFilters)">
            <div class="kpi-card__icon">
              <feather-icon icon="ArrowDownCircleIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Total Withdrawals') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalWithdrawals) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>
                {{ $t('Count') }}: {{ statistics.withdrawalsCount }}
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <!-- Net Deposits moved INTO the hero above (still clickable, delta + withdrawal rate kept) -->

        <div class="kpi-cell">
          <div class="kpi-card kpi-card--warning kpi-card--clickable" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
               @click="openCardTx($t('FTD'), { ...depositsFilters, ftdFamily: true, excludeRefundedFtdClients: true })">
            <div class="kpi-card__icon">
              <feather-icon icon="UserPlusIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('FTD') }}</div>
              <div class="kpi-card__value">{{ statistics.ftdCount }}</div>
              <div class="kpi-card__footer">
                <i class="fa-solid fa-coins mr-25" style="font-size: 11px;"></i>
                {{ convertToEURO(statistics.ftdAmount) }}
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <div class="kpi-cell">
          <div class="kpi-card kpi-card--txf kpi-card--clickable" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
               @click="openCardTx($t('STD'), { ...depositsFilters, isStd: true })">
            <div class="kpi-card__icon">
              <feather-icon icon="RefreshCwIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('STD') }}</div>
              <div class="kpi-card__value">{{ statistics.stdCount }}</div>
              <!-- STD as a share of the period's FTDs (no period-over-period delta here on purpose) -->
              <div class="kpi-card__delta kpi-card__delta--pct">
                <feather-icon icon="PercentIcon" size="12"/>
                {{ stdOfFtdPct }}% {{ $t('monetaryDashboard.ofFtds') }}
              </div>
              <div class="kpi-card__footer">
                <i class="fa-solid fa-coins mr-25" style="font-size: 11px;"></i>
                {{ convertToEURO(statistics.stdAmount) }}
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <!-- Pending approvals moved: deposits into the hero, withdrawals into
             the "Needs attention" panel below (both still open the review modal). -->

        <!-- TXF (taxes): always shown. Its money folds INTO Total Deposits /
             Withdrawals / Net unless the "Exclude TXF" filter is ticked. -->
        <div class="kpi-cell">
          <div class="kpi-card kpi-card--primary kpi-card--clickable" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.txfHint')"
               @click="openCardTx($t('monetaryDashboard.txf'), txfCardFilters)">
            <div class="kpi-card__icon">
              <i class="fa-solid fa-file-invoice-dollar" style="font-size: 22px;"></i>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('monetaryDashboard.txf') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.txfAmount) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>
                {{ $t('Count') }}: {{ statistics.txfCount }}
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </div>
      </div>

      <!-- Needs attention (Command Center layout): pending withdrawals + the
           FAKE money (approved transactions marked realType FAKE). Every tile
           opens its list; the FAKE tiles keep their dashed frame + badge so
           fake money is never misread as real money. -->
      <div class="mon-watch">
        <div class="mon-watch__title"><i class="fa-solid fa-bell mr-25"></i>{{ $t('commandCenter.attention') }}</div>
        <div class="mon-watch__grid">
          <button type="button" class="mon-watch__tile mon-watch__tile--wd" v-b-tooltip.hover
                  :title="$t('finance.clickToReview')" @click="openPending('WITHDRAWAL')">
            <span class="mon-watch__label"><i class="fa-solid fa-arrow-up mr-25"></i>{{ $t('monetaryDashboard.pendingWithdrawals') }}</span>
            <span class="mon-watch__count">{{ statistics.pendingWithdrawalsCount }}</span>
            <span class="mon-watch__amount">{{ convertToEURO(Math.abs(statistics.pendingWithdrawalsAmount || 0)) }}</span>
          </button>

          <button type="button" class="mon-watch__tile mon-watch__tile--fake" v-b-tooltip.hover
                  :title="$t('monetaryDashboard.viewTransactions')"
                  @click="openCardTx($t('Fake Deposits'), { ...depositsFilters, realType: ['FAKE'] })">
            <span class="mon-watch__badge">FAKE</span>
            <span class="mon-watch__label"><i class="fa-solid fa-coins mr-25"></i>{{ $t('Fake Deposits') }}</span>
            <span class="mon-watch__count">{{ convertToEURO(statistics.fakeDeposits) }}</span>
            <span class="mon-watch__amount">
              {{ $t('Count') }}: {{ statistics.fakeDepositsCount }}
            </span>
          </button>

          <button type="button" class="mon-watch__tile mon-watch__tile--fake" v-b-tooltip.hover
                  :title="$t('monetaryDashboard.viewTransactions')"
                  @click="openCardTx($t('Fake Withdrawals'), { ...withdrawalsFilters, realType: ['FAKE'] })">
            <span class="mon-watch__badge">FAKE</span>
            <span class="mon-watch__label"><feather-icon icon="ArrowDownCircleIcon" size="13" class="mr-25"/>{{ $t('Fake Withdrawals') }}</span>
            <span class="mon-watch__count">{{ convertToEURO(statistics.fakeWithdrawals) }}</span>
            <span class="mon-watch__amount">
              {{ $t('Count') }}: {{ statistics.fakeWithdrawalsCount }}
            </span>
          </button>

          <!-- Fake net: both kinds at once (no type filter) — the modal's sum is the net -->
          <button type="button" class="mon-watch__tile mon-watch__tile--fake" v-b-tooltip.hover
                  :title="$t('monetaryDashboard.viewTransactions')"
                  @click="openCardTx($t('Fake Net Deposits'), { ...depositsFilters, type: null, realType: ['FAKE'] })">
            <span class="mon-watch__badge">FAKE</span>
            <span class="mon-watch__label"><feather-icon icon="TrendingUpIcon" size="13" class="mr-25"/>{{ $t('Fake Net Deposits') }}</span>
            <span class="mon-watch__count">{{ convertToEURO(statistics.fakeNetDeposits) }}</span>
            <span class="mon-watch__amount">&nbsp;</span>
          </button>

          <button type="button" class="mon-watch__tile mon-watch__tile--fake" v-b-tooltip.hover
                  :title="$t('monetaryDashboard.viewTransactions')"
                  @click="openCardTx($t('Fake FTDs'), { ...depositsFilters, realType: ['FAKE'], isFtd: true })">
            <span class="mon-watch__badge">FAKE</span>
            <span class="mon-watch__label"><feather-icon icon="UserPlusIcon" size="13" class="mr-25"/>{{ $t('Fake FTDs') }}</span>
            <span class="mon-watch__count">{{ statistics.fakeFtdCount }}</span>
            <span class="mon-watch__amount">
              {{ convertToEURO(statistics.fakeFtdAmount) }}
            </span>
          </button>
        </div>
      </div>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50 d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{{ $t('Deposits') }}</h5>
          <b-button size="sm" variant="outline-primary" v-b-tooltip.hover :title="$t('columns.self')"
                    @click="openColumnsModal">
            <i class="fa-solid fa-bars"></i>
          </b-button>
        </b-card-header>
        <b-card-body>
          <monetaries-table v-bind:filters="depositsFilters" :columns="columns" :has-selection="false" trans-type="deposits"/>
        </b-card-body>
      </b-card>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50 d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{{ $t('Withdrawals') }}</h5>
          <b-button size="sm" variant="outline-primary" v-b-tooltip.hover :title="$t('columns.self')"
                    @click="openColumnsModal">
            <i class="fa-solid fa-bars"></i>
          </b-button>
        </b-card-header>
        <b-card-body>
          <monetaries-table v-bind:filters="withdrawalsFilters" :columns="columns" :has-selection="false" trans-type="withdrawals"/>
        </b-card-body>
      </b-card>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50">
          <h5 class="mb-0">{{ $t('Deposits & Withdrawals over time') }}</h5>
        </b-card-header>
        <b-card-body>
          <monetary-time-series-chart :points="statistics.series" :loading="loading" :is-dark-skin="isDarkSkin"/>
        </b-card-body>
      </b-card>

    </b-overlay>

    <!-- Pending approvals: review + approve/decline -->
    <b-modal v-model="pendingModalVisible" size="xl" centered hide-footer
             modal-class="pending-modal" @hidden="onPendingHidden">
      <!-- Title bar with the totals of the WHOLE pending set — count + money,
           same chips as the KPI drill modal -->
      <template #modal-title>
        {{ pendingModalTitle }}
        <span class="pm-chip pm-chip--count">
          <span class="pm-chip__label">{{ $t('menu.monetaries') }}</span>
          <span class="pm-chip__value">{{ pendingModalTotal }}</span>
        </span>
        <span class="pm-chip pm-chip--sum">
          <span class="pm-chip__label">{{ $t('salesDashboard.total') }}</span>
          <span class="pm-chip__value">{{ convertToEURO(Math.abs(pendingModalSum || 0)) }}</span>
        </span>
        <!-- ONE task covering the whole pending set -->
        <button type="button" class="pm-chip pm-chip--task" @click="createPendingTask">
          <i class="fa-solid fa-list-check mr-25"></i>{{ $t('tasks.taskForAll') }}
        </button>
      </template>
      <monetaries-table v-if="pendingModalVisible" :filters="pendingFilters" :columns="pendingColumns"
                        trans-type="pending" :has-selection="false" :lazy="false"
                        @total="v => pendingModalTotal = v" @sum="v => pendingModalSum = v"
                        @input="pendingDirty = true"/>
    </b-modal>

    <!-- KPI card drill-down: raw transactions behind the clicked number -->
    <insight-transactions-modal ref="txModal"/>

    <!-- ONE task for the whole pending set (from the pending review modal) -->
    <task-modal ref="pendingTaskModal"/>

    <!-- Per-table column show/hide/reorder (shared by the Deposits + Withdrawals tables) -->
    <columns-modal ref="columns-modal" v-model="columns" v-bind:columns-names="columnsNames"/>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import DeptQuickFilter from '@/components/widget/DeptQuickFilter';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import FilterCard from '@/components/widget/FilterCard';
import MonetariesTable from '@/components/tables/MonetariesTable';
import MonetaryTimeSeriesChart from '@/components/chart/MonetaryTimeSeriesChart';
import MonetaryDashboardSearchRequest from '@/model/Dashboard/MonetaryDashboardSearchRequest';
import MonetaryDashboardFilterFields from '@/model/filter/MonetaryDashboardFilterFields';
import InsightTransactionsModal from '@/components/modal/InsightTransactionsModal.vue';
import TaskModal from '@/components/modal/TaskModal.vue';
import ColumnsModal from '@/components/modal/columnsModal';
import Monetaries from '@/model/monetary/Monetary';

const REFRESH_DEBOUNCE_MS = 250;

export default {
  name: 'MonetaryDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],

  components: {
    CalendarBar,
    DeptQuickFilter,
    FilterCard,
    MonetariesTable,
    MonetaryTimeSeriesChart,
    InsightTransactionsModal,
    TaskModal,
    ColumnsModal,
  },

  data: () => ({
    filter: MonetaryDashboardSearchRequest(),
    filterFields: [],
    // Column show/hide/reorder prefs for the Deposits + Withdrawals tables
    // (persisted per-route in localStorage by columns-modal).
    columns: [],
    columnsNames: [
      ...Object.keys(Monetaries()),
      // approval time (operationTime — when the status decision happened)
      'operationTime',
      'clientDetails.accountId', 'clientDetails.clientId', 'clientDetails.email', 'clientDetails.country', 'clientDetails.affiliate',
      'paymentDetails.processorName', 'paymentDetails.comments',
    ],
    dateRange: { from: null, until: null },
    loading: false,
    inflight: false,
    pendingFetch: false,
    refreshTimer: null,
    pendingModalVisible: false,
    // Which half of the split pending card was clicked (DEPOSIT / WITHDRAWAL)
    pendingType: null,
    // True once an approve/decline happened inside the pending modal —
    // only then is a dashboard re-pull on close worth it.
    pendingDirty: false,
    // Totals of the whole pending set (from the modal's table) — title chips.
    pendingModalTotal: 0,
    pendingModalSum: 0,
    statistics: {
      totalDeposits: 0,
      depositsCount: 0,
      totalWithdrawals: 0,
      withdrawalsCount: 0,
      netDeposits: 0,
      ftdCount: 0,
      ftdAmount: 0,
      stdCount: 0,
      stdAmount: 0,
      pendingApprovalsCount: 0,
      pendingDepositsCount: 0,
      pendingWithdrawalsCount: 0,
      pendingDepositsAmount: 0,
      pendingWithdrawalsAmount: 0,
      // FAKE money (approved transactions marked realType FAKE)
      fakeDeposits: 0,
      fakeDepositsCount: 0,
      fakeWithdrawals: 0,
      fakeWithdrawalsCount: 0,
      fakeNetDeposits: 0,
      fakeFtdCount: 0,
      fakeFtdAmount: 0,
      // TXF (taxes) — own KPI, never inside the deposit totals
      txfAmount: 0,
      txfCount: 0,
      series: [],
      previous: null,
    },
  }),
  watch: {
    filter: {
      handler: 'scheduleRefresh',
      deep: true,
    },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    viewKey() {
      return this.$route.name || 'monetary-dashboard';
    },
    withdrawalRate() {
      const deposits = this.statistics.totalDeposits || 0;
      const withdrawals = this.statistics.totalWithdrawals || 0;
      if (!deposits) return '0.00';
      return (withdrawals / deposits * -100).toFixed(2);
    },
    // Mirrors the backend's pendingApprovalStats criteria EXACTLY (CREDIT_LINE
    // only, non-test, realOnly-aware, same date dimension) — so the list holds
    // the very rows the KPI counted. Bonus/credit/taxes and test rows are
    // pending too but the card never counts them, so the list must not either.
    pendingFilters() {
      return {
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        ...this.tableDateScope,
        realType: this.filter.realOnly === false ? null : ['REAL'],
        action: ['CREDIT_LINE'],
        isTest: false,
        // Both pending flavors: website REQUESTs + CRM-created PENDING rows.
        status: ['REQUEST', 'PENDING'],
        type: this.pendingType || null,
      };
    },
    pendingModalTitle() {
      const base = this.$t('Pending Approvals');
      if (this.pendingType === 'DEPOSIT') return `${base} · ${this.$t('monetaryDashboard.pendingDeposits')}`;
      if (this.pendingType === 'WITHDRAWAL') return `${base} · ${this.$t('monetaryDashboard.pendingWithdrawals')}`;
      return base;
    },
    pendingColumns() {
      return [
        { key: 'clientDetails.accountId', enable: true },
        { key: 'clientDetails.email', enable: true },
        { key: 'businessUnit', enable: true },
        { key: 'amount', enable: true },
        { key: 'type', enable: true },
        { key: 'paymentDetails.processorName', enable: true },
        { key: 'registrationTime', enable: true },
        { key: 'status', enable: true },
        { key: 'actions', enable: true },
      ];
    },
    // Approval-time filter set → it REPLACES the calendar (creation) range as
    // the date dimension, for the KPIs (backend) AND these tables alike.
    approvalFilterActive() {
      const ot = this.filter.operationTime || {};
      return !!(ot.start || ot.end);
    },
    tableDateScope() {
      return this.approvalFilterActive
          ? { registrationTime: { start: null, end: null }, operationTime: this.filter.operationTime }
          : {
            registrationTime: {
              start: this.$moment(this.dateRange.from).toServerDateTimeFormat(),
              end: this.$moment(this.dateRange.until).toServerDateTimeFormat(),
            },
            operationTime: { start: null, end: null },
          };
    },
    // STD as a share of the period's FTDs (100 FTD / 40 STD → 40%)
    stdOfFtdPct() {
      const ftd = this.statistics.ftdCount || 0;
      if (!ftd) return 0;
      return Math.round(((this.statistics.stdCount || 0) / ftd) * 100);
    },
    depositsFilters() {
      return {
        type: 'DEPOSIT',
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        ...this.tableDateScope,
        realType: this.filter.realOnly === false ? null : ['REAL'],
        status: ['APPROVED'],
        // TXF folds into the deposit KPIs unless excluded — the table below
        // must show the same rows or card and table disagree.
        action: this.filter.excludeTxf ? ['CREDIT_LINE'] : ['CREDIT_LINE', 'TAXES'],
        // Match the KPI aggregation, which always excludes test transactions
        // (isTest=false). Without this the table counts test deposits and
        // disagrees with the card count above.
        isTest: false,
      };
    },
    // TXF card drill-down: explicit action TAXES (which the backend lets
    // through even though the table hides TXF rows by default).
    txfCardFilters() {
      return {
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        ...this.tableDateScope,
        status: ['APPROVED'],
        action: ['TAXES'],
        isTest: false,
      };
    },
    withdrawalsFilters() {
      return {
        type: 'WITHDRAWAL',
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        ...this.tableDateScope,
        realType: this.filter.realOnly === false ? null : ['REAL'],
        status: ['APPROVED'],
        // Same TXF fold-in rule as the deposits table.
        action: this.filter.excludeTxf ? ['CREDIT_LINE'] : ['CREDIT_LINE', 'TAXES'],
        // Match the KPI aggregation, which always excludes test transactions.
        isTest: false,
      };
    },
    delta() {
      const prev = this.statistics.previous;
      if (!prev) return {};
      return {
        totalDeposits: this.computeDelta(this.statistics.totalDeposits, prev.totalDeposits),
        totalWithdrawals: this.computeDelta(this.statistics.totalWithdrawals, prev.totalWithdrawals),
        netDeposits: this.computeDelta(this.statistics.netDeposits, prev.netDeposits),
        ftdCount: this.computeDelta(this.statistics.ftdCount, prev.ftdCount),
        stdCount: this.computeDelta(this.statistics.stdCount, prev.stdCount),
        fakeDeposits: this.computeDelta(this.statistics.fakeDeposits, prev.fakeDeposits),
        fakeWithdrawals: this.computeDelta(this.statistics.fakeWithdrawals, prev.fakeWithdrawals),
        fakeNetDeposits: this.computeDelta(this.statistics.fakeNetDeposits, prev.fakeNetDeposits),
        fakeFtdCount: this.computeDelta(this.statistics.fakeFtdCount, prev.fakeFtdCount),
        txfAmount: this.computeDelta(this.statistics.txfAmount, prev.txfAmount),
      };
    },
  },
  created() {
    this.filterFields = MonetaryDashboardFilterFields();
    this.dateRange= {
      from: this.$moment().startOf('week').format('YYYY-MM-DD'),
      until: this.$moment().endOf('week').format('YYYY-MM-DD'),
    }
  },
  mounted() {
    this.$root.$on('dashboard::refresh', this.scheduleRefresh);
  },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  },
  methods: {
    ...mapActions('dashboard', ['getMonetaryDashboard']),
    openColumnsModal() {
      this.$refs['columns-modal'].showModal();
    },
    openPending(type) {
      this.pendingType = type || null;
      this.pendingDirty = false;
      this.pendingModalTotal = 0;
      this.pendingModalSum = 0;
      this.pendingModalVisible = true;
    },
    // ONE task covering the whole pending set shown in the review modal.
    createPendingTask() {
      this.$refs.pendingTaskModal.showModal(null, {
        type: 'HANDLE_TRANSACTION',
        relatedModule: 'MONETARY',
        title: `${this.pendingModalTitle} · ${this.pendingModalTotal} · ${this.convertToEURO(Math.abs(this.pendingModalSum || 0))}`,
        description: [
          `${this.$t('menu.monetaries')}: ${this.pendingModalTotal}`,
          `${this.$t('salesDashboard.total')}: ${this.convertToEURO(Math.abs(this.pendingModalSum || 0))}`,
          this.pendingType ? `${this.$t('columns.type')}: ${this.pendingType}` : null,
          `${this.$t('filters.registrationTime')}: ${this.dateRange.from || ''} → ${this.dateRange.until || ''}`,
        ].filter(Boolean).join('\n'),
        // The exact scope — the task detail re-queries it LIVE and offers the
        // standard table + bulk actions on the matching transactions.
        context: { monetaryFilters: { ...this.pendingFilters } },
      });
    },
    // KPI card → read-only list of the exact transactions the card counts.
    // (Filters mirror what the backend aggregation applies for that card.)
    openCardTx(title, filters) {
      this.$refs.txModal.show(title, filters);
    },
    onPendingHidden() {
      // Re-pull the dashboard ONLY when something was approved/declined
      // inside the modal — just looking and closing changes nothing.
      if (this.pendingDirty) {
        this.pendingDirty = false;
        this.scheduleRefresh();
      }
    },
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (!this.dateRange.from) return;
      if (this.inflight) {
        this.pendingFetch = true;
        return;
      }
      this.doFetch();
    },
    doFetch() {
      const payload = {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
      };
      this.inflight = true;
      this.loading = true;
      this.getMonetaryDashboard(payload)
        .then(
          data => this.setData(data),
          err => this.showServerErrorNotification('getMonetaryDashboard', 'dashboard', err)
        )
        .finally(() => {
          this.inflight = false;
          this.loading = false;
          if (this.pendingFetch) {
            this.pendingFetch = false;
            this.doFetch();
          }
        });
    },
    setData(data) {
      if (!data) return;
      this.statistics.totalDeposits = data.totalDeposits || 0;
      this.statistics.depositsCount = data.depositsCount || 0;
      this.statistics.totalWithdrawals = data.totalWithdrawals || 0;
      this.statistics.withdrawalsCount = data.withdrawalsCount || 0;
      this.statistics.netDeposits = data.netDeposits || 0;
      this.statistics.ftdCount = data.ftdCount || 0;
      this.statistics.ftdAmount = data.ftdAmount || 0;
      this.statistics.stdCount = data.stdCount || 0;
      this.statistics.stdAmount = data.stdAmount || 0;
      this.statistics.pendingApprovalsCount = data.pendingApprovalsCount || 0;
      this.statistics.pendingDepositsCount = data.pendingDepositsCount || 0;
      this.statistics.pendingWithdrawalsCount = data.pendingWithdrawalsCount || 0;
      this.statistics.pendingDepositsAmount = data.pendingDepositsAmount || 0;
      this.statistics.pendingWithdrawalsAmount = data.pendingWithdrawalsAmount || 0;
      // FAKE fields: fall back to 0 — older cached responses may not have them.
      this.statistics.fakeDeposits = data.fakeDeposits || 0;
      this.statistics.fakeDepositsCount = data.fakeDepositsCount || 0;
      this.statistics.fakeWithdrawals = data.fakeWithdrawals || 0;
      this.statistics.fakeWithdrawalsCount = data.fakeWithdrawalsCount || 0;
      this.statistics.fakeNetDeposits = data.fakeNetDeposits || 0;
      this.statistics.fakeFtdCount = data.fakeFtdCount || 0;
      this.statistics.fakeFtdAmount = data.fakeFtdAmount || 0;
      this.statistics.txfAmount = data.txfAmount || 0;
      this.statistics.txfCount = data.txfCount || 0;
      this.statistics.series = data.series || [];
      this.statistics.previous = data.previous || null;
    },
    computeDelta(current, previous) {
      const cur = Number(current) || 0;
      const prev = Number(previous) || 0;
      if (prev === 0) {
        if (cur === 0) return null;
        return { label: '—', positive: cur >= 0 };
      }
      const pct = ((cur - prev) / Math.abs(prev)) * 100;
      const positive = pct >= 0;
      const sign = positive ? '+' : '';
      return { label: `${sign}${pct.toFixed(1)}%`, positive };
    },
  },
};
</script>

<style scoped lang="scss">
/* Title chips on the pending review modal — count + money of the whole set,
   same look as the KPI drill modal's chips */
.pm-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-left: 0.6rem;
  padding: 0.18rem 0.7rem;
  border-radius: 999px;
  color: #fff;
  font-size: 0.78rem;
  vertical-align: middle;

  &--count { background: linear-gradient(135deg, #8e7cf5 0%, #7367f0 60%, #5e50ee 100%); }
  &--sum { background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%); }
  /* "task for all" button-chip */
  &--task {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
    border: none;
    cursor: pointer;
    font-weight: 700;
    transition: filter 0.15s ease;
    &:hover, &:focus { filter: brightness(1.1); outline: none; color: #fff; }
  }
  &__label { opacity: 0.85; font-weight: 600; }
  &__value { font-weight: 800; }
}

.monetary-dashboard {
  position: relative;

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

.kpi-grid {
  display: grid;
  /* Container-driven: columns wrap to the ACTUAL available width (whether the
     sidebar is open or not) instead of Bootstrap's viewport-based breakpoints,
     so a card is never squeezed narrow enough to truncate its amount. */
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: 0.75rem;
  /* bottom margin restores the spacing the old b-col `mb-2` gave before the
     FAKE strip below. */
  margin: 0.5rem 0 1rem;
}

.kpi-cell {
  display: flex;
}
/* The hero takes two grid columns (one on narrow screens) — like the Command Center */
.kpi-cell--hero { grid-column: span 2; }
@media (max-width: 640px) {
  .kpi-cell--hero { grid-column: span 1; }
}

.kpi-card {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28),
              0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 118px;

  /* calm hover (Command Center): a small lift on clickable cards only */
  &.kpi-card--clickable:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35),
                0 4px 8px rgba(20, 24, 50, 0.08);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
  }

  &__body {
    position: relative;
    z-index: 2;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.92;
    margin-bottom: 0.25rem;
    /* Wrap instead of truncating — a narrow card shows "Total Deposits" on two
       lines rather than "Total…". */
    white-space: normal;
    overflow-wrap: anywhere;
  }

  &__value {
    font-size: 1.8rem;
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.01em;
    /* Never clip the amount — let it wrap if the card is narrow. */
    white-space: normal;
    overflow-wrap: anywhere;
  }

  /* secondary figures (amount / count): readable, not a footnote */
  &__footer {
    margin-top: 0.45rem;
    font-size: 0.98rem;
    font-weight: 600;
    opacity: 0.96;
    display: flex;
    align-items: center;
  }

  &__delta {
    margin-top: 0.35rem;
    font-size: 0.8rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);

    &.is-up { color: #d6ffe8; }
    &.is-down { color: #ffe0e0; }
  }

  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }

  &--primary {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }

  &--success {
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }

  &--warning {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%);
  }

  &--danger {
    background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%);
  }

  &--pending {
    background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 55%, #5b21b6 100%);
  }

  /* Pending card: same shape, diagonally split into two clickable halves */
  &--split {
    padding: 0;
    min-height: 128px;

    .kpi-half {
      position: absolute;
      inset: 0;
      border: none;
      background: transparent;
      color: #fff;
      cursor: pointer;
      z-index: 2;
      padding: 0;
      /* The card itself lifts like every other KPI card — the halves only
         highlight, smoothly, so hovering never looks glitchy. */
      transition: background 0.18s ease;

      &:focus { outline: none; }

      /* top-left triangle (deposits) — diagonal runs top-right → bottom-left */
      &--dep {
        clip-path: polygon(0 0, 100% 0, 0 100%);
        background: rgba(255, 255, 255, 0.10);
        &:hover { background: rgba(255, 255, 255, 0.4); }
      }
      /* bottom-right triangle (withdrawals) */
      &--wd {
        clip-path: polygon(100% 0, 100% 100%, 0 100%);
        background: rgba(0, 0, 0, 0.16);
        &:hover { background: rgba(0, 0, 0, 0.45); }
      }

      &:hover .kpi-half__inner { transform: scale(1.08); }
    }

    .kpi-half__inner {
      position: absolute;
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      transition: transform 0.18s ease;
    }
    .kpi-half--dep .kpi-half__inner {
      top: 12px;
      left: 16px;
      align-items: flex-start;
      text-align: left;
      transform-origin: top left;
    }
    .kpi-half--wd .kpi-half__inner {
      bottom: 12px;
      right: 16px;
      align-items: flex-end;
      text-align: right;
      transform-origin: bottom right;
    }
    .kpi-half__title {
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.92;
      display: inline-flex;
      align-items: center;
    }
    .kpi-half__value {
      font-size: 1.4rem;
      font-weight: 800;
      line-height: 1.1;
    }
    /* the pending money itself, under the count */
    .kpi-half__amount {
      font-size: 0.82rem;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      opacity: 0.92;
      white-space: nowrap;
    }
  }

  &--txf {
    background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 55%, #0f766e 100%);
  }

  &--clickable {
    cursor: pointer;
    &:focus { outline: none; }
    &:hover .kpi-card__expand { opacity: 0.95; transform: scale(1.15); }
  }

  /* drill-down affordance (same idea as the uim-kpi--click tiles) */
  &__expand {
    position: absolute;
    top: 10px;
    right: 12px;
    z-index: 3;
    font-size: 0.72rem;
    opacity: 0;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
}

/* STD card: share-of-FTDs badge (replaces the period delta on that card) */
.kpi-card__delta--pct {
  background: rgba(0, 0, 0, 0.3) !important;
  color: #fff !important;
  font-weight: 800;
  font-size: 0.95rem;
  padding: 0.15rem 0.65rem;
}

/* ===== Hero (Command Center layout): deeper green, dark-glass inner pills ===== */
.mon-hero {
  align-items: flex-start;
  padding: 1rem 1.25rem 0.9rem;
  &.kpi-card--success { background: linear-gradient(135deg, #0ea371 0%, #047857 55%, #065f46 100%); }
  .kpi-card__title { color: rgba(255, 255, 255, 0.92); text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); display: flex; align-items: center; gap: 0.5rem; }
  .kpi-card__delta { background: rgba(0, 0, 0, 0.3); margin-top: 0; }
}
.mon-hero__body { position: relative; z-index: 2; width: 100%; min-width: 0; }
.mon-hero__main {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  color: inherit;
  padding: 0;
  cursor: pointer;
  &:focus { outline: none; }
  &:hover .mon-hero__expand { opacity: 1; transform: scale(1.1); }
}
.mon-hero__expand { font-size: 0.8rem; opacity: 0.6; transition: all 0.15s ease; }
.mon-hero__value {
  display: block;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: normal;
  overflow-wrap: anywhere;
}
.mon-hero__meta {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-top: 0.3rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  flex-wrap: wrap;
}
.mon-hero__net {
  margin-top: 0.6rem;
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.4rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(0, 0, 0, 0.4); }
  &:hover .mon-hero__expand { opacity: 1; }
  &:focus { outline: none; }
}
.mon-hero__net-label { font-size: 0.82rem; font-weight: 700; display: inline-flex; align-items: center; }
.mon-hero__net-value { font-size: 1.15rem; font-weight: 800; font-variant-numeric: tabular-nums; }
.mon-hero__net-rate { font-size: 0.8rem; font-weight: 600; opacity: 0.95; margin-left: auto; display: inline-flex; align-items: center; }
.mon-hero__pending {
  margin-top: 0.6rem;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 210, 127, 0.55);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(0, 0, 0, 0.4); }
  &:focus { outline: none; }
}
.mon-hero__pending-label { font-size: 0.85rem; font-weight: 700; flex: 1; text-align: left; color: #ffd27f; }
.mon-hero__pending-count { font-size: 1.25rem; font-weight: 800; font-variant-numeric: tabular-nums; }
.mon-hero__pending-amount { font-size: 0.9rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.mon-hero__pending-arrow { font-size: 0.75rem; opacity: 0.75; }

/* ===== Needs attention — pending withdrawals + FAKE tiles ===== */
.mon-watch {
  margin: 0 0 1rem;
  padding: 0.85rem 0.85rem 0.95rem;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfe 100%);
  border: 1px solid rgba(34, 41, 47, 0.08);
  box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.9),
      0 12px 30px -14px rgba(20, 24, 50, 0.22),
      0 2px 6px rgba(20, 24, 50, 0.06);
}
.mon-watch__title {
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5e5873;
  margin-bottom: 0.65rem;
}
.mon-watch__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 0.6rem;
}
.mon-watch__tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  border: 1px solid transparent;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 8px 18px -8px rgba(20, 24, 50, 0.35); }
  &:focus { outline: none; }
  &--wd {
    background: rgba(234, 84, 85, 0.1);
    border-color: rgba(234, 84, 85, 0.35);
    .mon-watch__label, .mon-watch__amount { color: #d64545; }
  }
  &--fake {
    background: repeating-linear-gradient(135deg, rgba(240, 134, 43, 0.1) 0 8px, rgba(240, 134, 43, 0.05) 8px 16px);
    border: 1px dashed rgba(240, 134, 43, 0.7);
    .mon-watch__label, .mon-watch__amount { color: #c9641a; }
  }
}
.mon-watch__badge {
  position: absolute;
  top: 6px;
  right: 8px;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: #ff9f43;
  color: #fff;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.14em;
}
.mon-watch__label {
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-flex;
  align-items: center;
}
.mon-watch__count {
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: #24292f;
}
.mon-watch__amount { font-size: 0.88rem; font-weight: 700; font-variant-numeric: tabular-nums; display: inline-flex; align-items: center; gap: 0.35rem; flex-wrap: wrap; }
.mon-watch__delta {
  display: inline-flex;
  align-items: center;
  gap: 0.1rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  font-size: 0.72rem;
  font-weight: 700;
  &.is-up { color: #1f9d61; }
  &.is-down { color: #d64545; }
}
.dark-layout {
  .mon-watch { background: linear-gradient(180deg, #283046 0%, #242b3d 100%); border-color: rgba(255, 255, 255, 0.08); }
  .mon-watch__title { color: #b4b7bd; }
  .mon-watch__count { color: #d0d2d6; }
  .mon-watch__delta { background: rgba(255, 255, 255, 0.1); }
}

/* FAKE money strip: a row of amber pills — clearly smaller than the real-money
   cards (so fake money can't be misread) but sized to be noticed at a glance.
   White-on-amber-gradient, so no dark-skin overrides are needed (same as the
   gradient KPI cards above). */
.fake-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  margin: 0 0 1.15rem;
  padding: 0.55rem 0.75rem;
  border-radius: 14px;
  border: 1px dashed rgba(240, 134, 43, 0.55);
  background: repeating-linear-gradient(135deg, rgba(240, 134, 43, 0.08) 0 8px, rgba(240, 134, 43, 0.03) 8px 16px);

  &__badge {
    padding: 0.25rem 0.7rem;
    border-radius: 999px;
    border: 1px dashed rgba(255, 255, 255, 0.75);
    background: #ff9f43;
    color: #fff;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.16em;
    line-height: 1.6;
    box-shadow: 0 2px 6px rgba(240, 134, 43, 0.35);
  }
}

.fake-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.95rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #ffb75d, #f0862b);
  color: #fff;
  font-size: 0.86rem;
  line-height: 1.2;
  box-shadow: 0 3px 10px rgba(240, 134, 43, 0.35);

  &__label { opacity: 0.94; font-weight: 700; }
  &__value { font-weight: 800; font-size: 1rem; }
  &__count { opacity: 0.88; font-size: 0.78rem; }

  &__delta {
    display: inline-flex;
    align-items: center;
    gap: 0.1rem;
    padding: 0 0.4rem;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.18);
    font-size: 0.7rem;
    font-weight: 700;

    &.is-up { color: #d6ffe8; }
    &.is-down { color: #ffe0e0; }
  }

  &--click {
    cursor: pointer;
    &:hover { filter: brightness(1.06); }
    &:focus { outline: none; }
  }
}

@media (max-width: 576px) {
  .kpi-card {
    min-height: auto;
    padding: 1rem;
    &__value { font-size: 1.3rem; }
  }
}
</style>
